import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
} from '@material-ui/core';
import {
    PublishOutlined as UploadIcon,
    DeleteOutline as DeleteIcon,
} from '@material-ui/icons';

import { Typography } from '../../../components/Wrappers/Wrappers.jsx';
import { DEFAULT_IMAGE } from '../../../commons/imageUploader/DefaultImage';
import useStyles from './PhotoUpload.styles';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

function isFileTooLarge (file) {
    return file.size > MAX_FILE_SIZE_BYTES;
}

function readFileAsDataURL (file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

function getImageSource (imageSource) {
    return imageSource || DEFAULT_IMAGE;
}

function getAltImageSource (imageSource) {
    return imageSource ? 'ECAL World Cover Photo' : 'No photo uploaded';
}

function getClassName (imageSource, classes) {
    return imageSource ? classes.image : classes.imagePlaceholder;
}

function PhotoUpload (props) {
    const {
        title,
        description,
        imageSource,
        onUpload,
        onDelete,
    } = props;

    const classes = useStyles();
    const fileInputRef = useRef(null);
    const [sizeError, setSizeError] = useState('');

    async function handleFileChange (e) {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        if (isFileTooLarge(file)) {
            setSizeError('Image is too large — please choose a file under 5MB.');
            return;
        }
        setSizeError('');
        try {
            const dataUrl = await readFileAsDataURL(file);
            onUpload(dataUrl, file);
        } catch (error) {
            console.error('PhotoUpload.handleFileChange() -> readFileAsDataURL failed', error);
        }
    }

    return (
        <div>
            { title && (
                <Typography variant={'body1'} className={classes.imageTitle}>
                    <b>{ title }</b>
                    { description && <><br/>{ description }</> }
                </Typography>
            )}

            <div className={classes.logoDisplay}>
                <div className={classes.imageBox}>
                    <img
                        src={getImageSource(imageSource)}
                        alt={getAltImageSource(imageSource)}
                        className={getClassName(imageSource, classes)}
                    />
                </div>

                <div className={classes.imageControls}>
                    <Typography variant={'body2'} className={classes.imageControlsLabel}>
                        Cover image
                    </Typography>

                    <div className={classes.imageControlsButtons}>
                        <Button
                            variant={'outlined'}
                            startIcon={<UploadIcon fontSize={'small'} />}
                            onClick={() => fileInputRef.current.click()}
                        >
                            Upload
                        </Button>

                        <Button
                            variant={'outlined'}
                            startIcon={<DeleteIcon fontSize={'small'} />}
                            style={{ color: '#B42318', borderColor: '#D0D5DD' }}
                            disabled={!imageSource}
                            onClick={onDelete}
                        >
                            Delete
                        </Button>
                    </div>
                    <Typography className={classes.imageHint}>
                        Upload a JPG or PNG image, 300px (width) x 250px (height), 6:5 ratio required.
                    </Typography>
                </div>

                <input
                    ref={fileInputRef}
                    type={'file'}
                    accept={'image/jpeg,image/png'}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </div>

            { sizeError && (
                <Typography variant={'body2'} className={classes.errorText}>
                    { sizeError }
                </Typography>
            )}
        </div>
    );
}

PhotoUpload.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    imageSource: PropTypes.string,
    onUpload: PropTypes.func,
    onDelete: PropTypes.func,
};

PhotoUpload.defaultProps = {
    title: '',
    description: '',
    imageSource: '',
    onUpload: () => {},
    onDelete: () => {},
};

export default PhotoUpload;
